import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import * as THREE from '@phdsoft/three';
import { Injectable } from "@angular/core";
import { AssetManager } from "src/app/asset-manager/asset-manager";
import { Graphics } from "./graphics";
import { Util } from '@phdsoft/common';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { WorkableElement } from 'src/app/platform/busines-data/SectionElements/WorkableElement';
import { DTSelectionService } from '../selection/selection.service';
import { GaugingElement } from 'src/app/platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants';
import { ThreeJsCleanup } from 'src/app/shared/utils/threejs-cleanup.util';
import { InspectionPointInstanceManager } from './inspection-point-instance-manager.service';
import { StructuralElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { buildRectangleFrustumPlanes, isInsideRectangleFrustum } from './selection-frustum';

interface selectedFilter {
  startDate: Date,
  endDate: Date
}

interface Rectd {
  init: boolean;
  buttonDown: boolean;
  geom?: THREE.Geometry;
  mesh?: THREE.Mesh;
  origx: number;
  origy: number;
  inix: number;
  endx: number;
  iniy: number;
  endy: number;
  startRectangleSelection: boolean;
}

// class cretaed based on graphics render repair
@Injectable({ providedIn: 'root' })
export class GraphicsMultipleSelection {
  constructor(
    private graphics: Graphics,
    private assetManager: AssetManager,
    private selectionService: DTSelectionService,
    private instanceManager: InspectionPointInstanceManager,
  ) { }

  private asset: Asset;
  distancePoints = [];

  // Pooled objects for screenToLocal to avoid per-call allocations
  private _pooledRaycaster = new THREE.Raycaster();
  private _pooledNdc = new THREE.Vector2();

  // Reusable BufferGeometry for the visible drag rectangle
  private _dragRectPositions = new Float32Array(12);
  private _dragRectGeometry: THREE.BufferGeometry | null = null;
  private _dragRectMesh: THREE.Mesh | null = null;

  xDirection: string = "+";
  yDirection: string = "+";

  xSignal: number = 1;
  ySignal: number = 1;

  public get selectedInspections(): Array<WorkableElement> {
    return this.assetManager.selectedInspections;
  };

  public set selectedInspections(value: Array<WorkableElement>) {
    this.assetManager.selectedInspections = value;
  }

  private _selectedFilter: selectedFilter = null;

  public get selectedFilter(): selectedFilter {
    return this._selectedFilter;
  }
  public set selectedFilter(filter: selectedFilter) {
    this._selectedFilter = filter;
  }

  private _rectd: Rectd = {
    init: false,
    buttonDown: false,
    geom: undefined,
    mesh: undefined,
    origx: 0,
    origy: 0,
    inix: 0,
    endx: 0,
    iniy: 0,
    endy: 0,
    startRectangleSelection: false,
  };

  public get rectd() {
    return this._rectd;
  }
  public set rectd(value) {
    this._rectd = value;
  }

  private resetRectd(): void {
    this.graphics.isDragMode = false;
    if (this._dragRectMesh) {
      this.graphics.camera.remove(this._dragRectMesh);
    }
    if (this._dragRectGeometry) {
      this._dragRectGeometry.dispose();
    }
    this._dragRectGeometry = null;
    this._dragRectMesh = null;

    // Dispose of objects that need cleanup
    ThreeJsCleanup.disposeObject(this._rectd.geom); // Dispose of the geometry
    this._rectd.geom = undefined;

    ThreeJsCleanup.disposeObject(this._rectd.mesh); // Dispose of the mesh
    this._rectd.mesh = undefined;

    // Reset the object to its initial configuration
    this._rectd = {
        init: false,
        buttonDown: false,
        geom: undefined,
        mesh: undefined,
        origx: 0,
        origy: 0,
        inix: 0,
        endx: 0,
        iniy: 0,
        endy: 0,
        startRectangleSelection: false,
    };
  }


  public screenToLocal(screenX, screenY, localZ) {
    this._pooledNdc.set(
      (screenX / window.innerWidth) * 2 - 1,
      (-screenY / window.innerHeight) * 2 + 1
    );
    this._pooledRaycaster.setFromCamera(this._pooledNdc, this.graphics.camera);
    let newpos = new THREE.Vector3();
    this._pooledRaycaster.ray.at(localZ, newpos);
    this.graphics.camera.worldToLocal(newpos);
    return newpos;
  }

  public threeStartGeometryCloning() {
    this.graphics.isDragMode = false;
    if (!this._rectd.startRectangleSelection) {
      this._rectd.startRectangleSelection = true
      this.graphics.Controls.enabled = false
    }
  }

  public updateMousePosition(event, asset: Asset) { //chamado toda vez que o mouse se movimenta
    event = event.changedTouches ? event.changedTouches[0] : event
    if (Util.isNullOrUndefined(event.clientX)) {
      event.clientX = event.center.x;
    }
    if (Util.isNullOrUndefined(event.clientY)) {
      event.clientY = event.center.y;
    }
    this.rectdDrawCloningGeometry(event.clientX - this.graphics.offsetX, event.clientY - this.graphics.offsetY, event.buttons, asset)
  }

  /*
 *  @brief: este método cria o quadrado de caixa de seleção para
 *          delimitar o reparo que queremos fazer. Ele tem 4 etapas (ifs),
 *          a primeira delimita quando começamos e as 3 ultimas são a
 *          seleção de fato, com o startdrag, stilldrag e enddrag
 */
  public rectdDrawCloningGeometry(mouseX, mouseY, buttons, asset: Asset) {
    this.asset = asset;
    if (this._rectd.startRectangleSelection) {
      if (buttons == 1) {
        this._rectd.startRectangleSelection = false
        this._rectd.init = true
      }
    }

    // Without `init` the still/end-drag branches below never fire, which previously
    // left `buttonDown=true` and `isDragMode=true` stuck on a plain click and
    // permanently disabled the OutlinePass (it gates on `!isDragMode`).
    if (!this._rectd.init) return;

    if (!this._rectd.buttonDown && buttons == 1) {
      // start dragging

      this._rectd.buttonDown = true
      this.graphics.isDragMode = true;

      // Create reusable drag rectangle (zero out old positions first)
      this._dragRectPositions.fill(0);
      this._dragRectGeometry = new THREE.BufferGeometry();
      const posAttr = new THREE.BufferAttribute(this._dragRectPositions, 3);
      posAttr.setUsage(THREE.DynamicDrawUsage);
      this._dragRectGeometry.setAttribute('position', posAttr);
      this._dragRectGeometry.setIndex([0, 1, 2, 0, 2, 3]);
      this._dragRectMesh = new THREE.Mesh(this._dragRectGeometry, GraphicsMaterials.selectionBoxMaterial);
      this._dragRectMesh.frustumCulled = false;
      this.graphics.camera.add(this._dragRectMesh);

    } else if (this._rectd.buttonDown && buttons == 1 && this._rectd.init) {
      // still dragging — update rectangle position only

      if (this._rectd.origx < mouseX) {
        this._rectd.inix = this._rectd.origx;
        this._rectd.endx = mouseX;
        this.xDirection = "+";
      } else {
        this._rectd.inix = mouseX;
        this._rectd.endx = this._rectd.origx;
        this.xDirection = "-";
      }

      if (this._rectd.origy < mouseY) {
        this._rectd.iniy = this._rectd.origy;
        this._rectd.endy = mouseY;
        this.yDirection = "+";
      } else {
        this._rectd.iniy = mouseY;
        this._rectd.endy = this._rectd.origy;
        this.yDirection = "-";
      }

      const distz = 1;
      const p0 = this.screenToLocal(this._rectd.inix, this._rectd.iniy, distz);
      const p1 = this.screenToLocal(this._rectd.inix, this._rectd.endy, distz);
      const p2 = this.screenToLocal(this._rectd.endx, this._rectd.endy, distz);
      const p3 = this.screenToLocal(this._rectd.endx, this._rectd.iniy, distz);

      const pos = this._dragRectPositions;
      pos[0] = p0.x; pos[1] = p0.y; pos[2] = p0.z;
      pos[3] = p1.x; pos[4] = p1.y; pos[5] = p1.z;
      pos[6] = p2.x; pos[7] = p2.y; pos[8] = p2.z;
      pos[9] = p3.x; pos[10] = p3.y; pos[11] = p3.z;

      // Store vertices for selectPointsByType (needs Geometry.vertices)
      this._rectd.geom = new THREE.Geometry();
      this._rectd.geom.vertices.push(p0, p1, p2, p3);

      (this._dragRectGeometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
      this.graphics.requestRender();

    } else if (this._rectd.buttonDown && buttons == 0 && this._rectd.init) {
      // end dragging
      this._rectd.buttonDown = false
      this.graphics.isDragMode = false;
      this.graphics.Controls.enabled = true

      try {
        this.selectPointsByType();
      } finally {
        // Clean up drag rectangle — guaranteed even if selectPointsByType throws
        if (this._dragRectMesh) {
          this.graphics.camera.remove(this._dragRectMesh);
          this._dragRectGeometry.dispose();
          this._dragRectGeometry = null;
          this._dragRectMesh = null;
        }

        this.resetRectd();
      }
    }

  }

  public selectPointsByType() {

    let planes
    {
      // Rectangle's 4 corners position in world space.
      //  Convention: The rectangle's corner indices range from 0 to 3 respectively from top-left to top-right, anticlockwise
      let rect = [...this._rectd.geom.vertices].map((v) =>
        this.graphics.camera.localToWorld(v),
      )
      // 4 planes through the camera and each rectangle edge, oriented inwards so
      // that only the pyramid IN FRONT of the camera is accepted (the mirrored
      // pyramid behind the camera used to pass the old "all <= 0" branch).
      planes = buildRectangleFrustumPlanes(this.graphics.camera.position, rect)

      // set x and y signal
      if (this._rectd.endx - this._rectd.inix < 0) {
        this.xSignal = -1;
      } else {
        this.xSignal = 1;
      }

      if (this._rectd.endy - this._rectd.iniy < 0) {
        this.ySignal = -1;
      } else {
        this.ySignal = 1;
      }
    }

    // Structural multi-selection: with structural elements selected, the
    // rectangle adds every visible element of the same type(s) to the selection
    // instead of looking for inspection points.
    const structuralAnchors = this.structuralSelectionAnchors();
    if (structuralAnchors.length > 0) {
      this.selectStructuralElementsInRectangle(planes, structuralAnchors);
      return;
    }

    let allPlates: Array<WorkableElement> = this.asset.getSectionInspectionPoints();

    if (this.assetManager.currentSelectedElement) {
      this.selectionService.clearElementSelected(null, this.assetManager.asset);
    } else {
      // Defensive: clear orphaned proxies/outlines from a previous failed selection
      // that never set currentSelectedElement (e.g. section lookup miss).
      this.instanceManager.releaseAllProxies();
      this.graphics.clearOutlinedElements(true);
    }

    this.selectedInspections = [];
    allPlates.forEach((point) => {
      // Explicit visibility gate for instanced points (SEL-01 / box multi-select).
      // point.visible calls GaugingElement.realVisibility which queries the manager,
      // but the explicit getInstanceVisible check provides defense-in-depth for the
      // box-select path (hidden instances are zero-scale and must be excluded).
      if (!this.instanceManager.getInstanceVisible(point as GaugingElement)) {
        return; // skip hidden instances (date-filtered, zero-scale)
      }
      if ((point as GaugingElement).inspectionMethodId === (this.selectionService.selectedWorkableElement as GaugingElement)?.inspectionMethodId &&
        (point as GaugingElement).targetElementType === (this.selectionService.selectedWorkableElement as GaugingElement)?.targetElementType &&
        this.isElementInSelectedFilter(point as GaugingElement) &&
        point.visible == true
      ) {
        if ((point as GaugingElement).inspectionMethodId === INSERTIONCONSTANTS.AREA || (point as GaugingElement).inspectionMethodId === INSERTIONCONSTANTS.ELEMENT) {
          // area or element: compute distance from the center of bounding box to the planes
          let center = new THREE.Vector3();
          point.SurfaceDesign.geometry.computeBoundingBox();
          point.SurfaceDesign.geometry.boundingBox.getCenter(center);
          if (isInsideRectangleFrustum(center, planes)) {
            this.selectedInspections.push(point);
          }
        }
        else if (
          isInsideRectangleFrustum(point.SurfaceDesign.position, planes) &&
          ((point as GaugingElement).inspectionMethodId == INSERTIONCONSTANTS.POINT || (point as GaugingElement)?.elementResult?.ElementInfo?.vertices?.length == 3) &&
          (point as GaugingElement).scope === (this.selectionService.selectedWorkableElement as GaugingElement)?.scope
        ) {
          this.selectedInspections.push(point);
        }
      }

    })

    // sort the points selected by rect
    this.selectedInspections = this.selectedInspections.sort(
      (a, b) => this.compareWorkableElements(a, b)
    );

    if (this.selectedInspections.length > 0) {
      this.selectedInspections = this.selectedInspections.filter(x => x.SectionId == this.assetManager.currentSection?.Guid)
      if(this.selectedInspections == null || this.selectedInspections.length === 0)return;
      // Set selectedObject to first element's SurfaceDesign so markElementSelected
      // can resolve the section from PhantomSurfaceDesign.userData.SectionID.
      this.selectionService.selectedObject = this.selectedInspections[0].SurfaceDesign;
      this.selectionService.markElementSelected(this.selectedInspections[0].Guid, this.assetManager.asset);
      let secSelectedIds = []
      for (let i = 1; i < this.selectedInspections.length; i++) {
        secSelectedIds.push(this.selectedInspections[i].Guid)
      }
      this.selectionService.markSecondaryElementsSelected(secSelectedIds, this.assetManager.asset);
    }
  }

  /** The structural elements the rectangle should extend (current multi-selection or the single selected element). */
  private structuralSelectionAnchors(): StructuralElement[] {
    const selected = (this.assetManager.selectedElements ?? []).filter(e => e instanceof StructuralElement) as StructuralElement[];
    if (selected.length > 0) {
      return selected;
    }
    const current = this.assetManager.currentSelectedElement;
    return current instanceof StructuralElement ? [current] : [];
  }

  /**
   * Adds to the selection every visible, non-repair structural element that
   * shares a type with one of the anchors, belongs to one of their regions
   * and whose bounding-box centre lies inside the drawn rectangle.
   */
  private selectStructuralElementsInRectangle(planes: THREE.Plane[], anchors: StructuralElement[]): void {
    const typeIds = new Set(anchors.map(a => a.TypeId));
    const anchorRegions = new Set(anchors.flatMap(a => a.regionIds ?? (a.regionId ? [a.regionId] : [])));
    const alreadySelected = new Set(anchors.map(a => a.Guid));

    const candidates = this.asset.getStructureAndRepairsElements(true).filter(e =>
      e instanceof StructuralElement &&
      !e.isRepair &&
      !alreadySelected.has(e.Guid) &&
      // With "Select more" on, the Structure Type list decides; otherwise the anchors' types.
      (this.assetManager.multiSelectEditActive
        ? this.assetManager.isTypeAllowedForMultiSelect(e)
        : typeIds.has(e.TypeId)) &&
      (e.regionIds ?? (e.regionId ? [e.regionId] : [])).some(r => anchorRegions.has(r)) &&
      !!e.SurfaceDesign?.geometry
    ) as StructuralElement[];

    const inside = candidates.filter(e => this.isCenterInsideRectangle(e, planes));
    if (inside.length === 0) {
      return;
    }
    this.selectionService.addStructuralElementsToSelection(inside, this.assetManager.asset);
  }

  private isCenterInsideRectangle(element: WorkableElement, planes: THREE.Plane[]): boolean {
    const geometry = element.SurfaceDesign.geometry;
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox();
    }
    const center = new THREE.Vector3();
    geometry.boundingBox.getCenter(center);
    element.SurfaceDesign.localToWorld(center);
    return isInsideRectangleFrustum(center, planes);
  }

  public setNextSelectedPointActive(element) {
    let index: number = -1;
    if (element.Guid) {
      index = this.selectedInspections.findIndex(x => x.Guid === element.Guid);
    }
    this.assetManager.deletePointFromTempArray(element);
    this.assetManager.deletePointFromSelectedPointsArray(element);

    if (this.selectionService.selectedWorkableElement) {
      this.selectionService.clearElementSelected(this.selectionService.selectedWorkableElement.Guid, this.assetManager.asset, false);
    }

    if (this.selectedInspections.length > 0) {
      if (this.selectedInspections[index]) {
        this.selectionService.markElementSelected(this.selectedInspections[index].Guid, this.assetManager.asset);
      } else {
        this.selectionService.markElementSelected(this.selectedInspections[0].Guid, this.assetManager.asset);
      }
    }
  }

  private isElementInSelectedFilter(element: GaugingElement) {
    if (this.selectedFilter == null) {
      return true;
    }
    for (const checkList of element.checkList) {
      let formattedDate = new Date(new Date(checkList.Date).toDateString());
      if (
        formattedDate >= this.selectedFilter.startDate &&
        formattedDate <= this.selectedFilter.endDate
      ) {
        return true;
      }
    }
    return false;
  }

  private compareWorkableElements(a: WorkableElement, b: WorkableElement) {
    let positionXOfA: number;
    let positionYOfA: number;

    let positionXOfB: number;
    let positionYOfB: number;

    if (this.isElementOrArea(a as GaugingElement) || this.isElementOrArea(b as GaugingElement)) {
      // area or element
      let center = new THREE.Vector3();

      a.SurfaceDesign.geometry.boundingBox.getCenter(center);
      let screenPosition = this.WorldToScreen(center.clone());
      positionXOfA = screenPosition.x;
      positionYOfA = screenPosition.y;

      b.SurfaceDesign.geometry.boundingBox.getCenter(center);
      screenPosition = this.WorldToScreen(center.clone());
      positionXOfB = screenPosition.x;
      positionYOfB = screenPosition.y;
    } else {
      // point
      let ScreenPositionOfA = this.WorldToScreen(a.SurfaceDesign.position.clone())
      let ScreenPositionOfB = this.WorldToScreen(b.SurfaceDesign.position.clone())
      positionXOfA = ScreenPositionOfA.x;
      positionYOfA = ScreenPositionOfA.y;
      positionXOfB = ScreenPositionOfB.x;
      positionYOfB = ScreenPositionOfB.y;
    }

    if (this.xDirection === "+" && this.yDirection === "+") {
      // console.log("-------------- first case -------------------")
      if (this.ASmallerThanB(positionXOfA, positionXOfB)) {
        return -1;
      } else if (this.ALargerThanB(positionXOfA, positionXOfB)) {
        return 1;
      }

      if (this.ASmallerThanB(positionYOfA, positionYOfB)) {
        return -1;
      } else if (this.ALargerThanB(positionYOfA, positionYOfB)) {
        return 1;
      }
    } else if (this.xDirection === "-" && this.yDirection === "+") {
      // console.log("-------------- second case -------------------")
      if (this.ALargerThanB(positionXOfA, positionXOfB)) {
        return -1;
      } else if (this.ASmallerThanB(positionXOfA, positionXOfB)) {
        return 1;
      }

      if (this.ASmallerThanB(positionYOfA, positionYOfB)) {
        return -1;
      } else if (this.ALargerThanB(positionYOfA, positionYOfB)) {
        return 1;
      }
    } else if (this.xDirection === "+" && this.yDirection === "-") {
      // console.log("-------------- third case -------------------")
      if (this.ASmallerThanB(positionXOfA, positionXOfB)) {
        return -1;
      } else if (this.ALargerThanB(positionXOfA, positionXOfB)) {
        return 1;
      }

      if (this.ALargerThanB(positionYOfA, positionYOfB)) {
        return -1;
      } else if (this.ASmallerThanB(positionYOfA, positionYOfB)) {
        return 1;
      }

    } else if (this.xDirection === "-" && this.yDirection === "-") {
      // console.log("-------------- fourth case -------------------")
      if (this.ALargerThanB(positionXOfA, positionXOfB)) {
        return -1;
      } else if (this.ASmallerThanB(positionXOfA, positionXOfB)) {
        return 1;
      }

      if (this.ALargerThanB(positionYOfA, positionYOfB)) {
        return -1;
      } else if (this.ASmallerThanB(positionYOfA, positionYOfB)) {
        return 1;
      }

    }

    return 0;
  }

  private isElementOrArea(element: GaugingElement): boolean {
    return element.inspectionMethodId && (element.inspectionMethodId === INSERTIONCONSTANTS.AREA || element.inspectionMethodId === INSERTIONCONSTANTS.ELEMENT);
  }

  private WorldToScreen(position: THREE.Vector3) {
    position.project(this.graphics.camera);
    const _screen = new THREE.Vector2((position.x + 1) * window.innerWidth / 2, - (position.y - 1) * window.innerHeight / 2);
    return _screen;
  }

  private ASmallerThanB(a, b): boolean {
    return (b - a) > 5;
  }

  private ALargerThanB(a, b): boolean {
    return (a - b) > 5;
  }
}
